import { I18n } from "@assets/strings";
import React from "react";
import styled, { useTheme } from "styled-components";
import incentiveImage from "@assets/images/group/incentive.png";
import incentiveImageJaangal from "@assets/images/variant-jaangal/post/incentive2.png";
import { Environment } from "@core/config/environment";
import { useServices } from "@core/servicesContext";
import { Group } from "@domain/group/group";
import { useAsync } from "@utils/hooks";
import { useAuthModalBlocker } from "@ui/components/auth/useAuthModalBlocker";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { PrimaryBold } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { FollowIcon } from "@ui/components/icons";
import { gap, mobile } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";

interface UnauthorizedGroupProps extends React.HTMLAttributes<HTMLDivElement> {
	group: Group;
}

export const UnauthorizedGroup: React.FC<UnauthorizedGroupProps> = ({ group, ...htmlProps }) => {
	const pending = group.status === "PENDING_HOST_ACCEPTANCE";
	const open = group.accessMode === "OPEN";

	const theme = useTheme();
	const { groupService } = useServices();
	const { run, loading } = useAsync(async () => {
		try {
			await groupService.requestMembership(group.id, open);
			createToast(
				I18n.t("success"),
				I18n.t(open ? `group.${group.type}.join.success` : "group.request.success", { name: group.name }),
				"success"
			);
		} catch {
			createToast(I18n.t("error"), I18n.t("group.request.error"), "error");
		}
	}, [group.id, group.type, open, group.name]);
	const blockGuest = useAuthModalBlocker();

	return (
		<Container {...htmlProps}>
			<PrimaryBold>
				{I18n.t(
					open
						? `group.${group.type}.welcome`
						: pending
							? `group.${group.type}.pending`
							: `group.${group.type}.unauthorized`
				)}
			</PrimaryBold>
			{!pending && (
				<LoadingButton loading={loading} onClick={() => blockGuest(run)}>
					<FollowIcon color={theme.colors.white} />
					<span>{I18n.t(open ? "group.join.button" : "group.join.request")}</span>
				</LoadingButton>
			)}
		</Container>
	);
};

const Container = styled(RoundedBlock)`
	${gap(25)};
	padding: 55px 200px 45px 30px;
	> :first-child {
		max-width: 200px;
	}
	@media (min-width: 1100px) {
		padding: 55px 200px 45px 50px;
		> :first-child {
			max-width: 300px;
		}
	}
	@media ${mobile} {
		padding: 45px 10px 45px 10px;
		background-image: none;
		display: flex;
		flex-direction: column;
		align-items: center;
		text-align: center;
	}
	background-image: url(${Environment.APP_VARIANT === "jaangal" ? incentiveImageJaangal : incentiveImage});
	background-size: ${Environment.APP_VARIANT === "jaangal" ? "contain" : "670px"};
	background-position: right;
	background-repeat: no-repeat;
	border: none;

	svg {
		margin-right: 8px;
	}
`;
